<template>
  <div class="theme-config">
    <div class="config-header">
      <h2 class="config-title">Theme Configuration</h2>
      <p class="config-subtitle">Customise your casino's branding — logo, colors, fonts, and background</p>
    </div>

    <div v-if="loading" class="loading-state">Loading theme settings…</div>
    <div v-else-if="error" class="error-banner">{{ error }}</div>

    <template v-else>
      <!-- Theme Presets -->
      <section class="config-section">
        <h3 class="section-title">Quick Start Presets</h3>
        <p class="section-desc">Click a preset to load its colors and fonts, then save to apply.</p>
        <div v-if="presetsLoading" class="loading-state loading-state--sm">Loading presets…</div>
        <div v-else class="presets-grid">
          <button
            v-for="preset in presets"
            :key="preset.id"
            class="preset-card"
            :class="{ 'preset-card--active': activePresetId === preset.id }"
            type="button"
            @click="applyPreset(preset)"
          >
            <div class="preset-swatches">
              <span
                v-for="(color, i) in preset.thumbnail_colors"
                :key="i"
                class="preset-swatch"
                :style="{ background: color }"
              />
            </div>
            <span class="preset-name">{{ preset.name }}</span>
            <span class="preset-desc">{{ preset.description }}</span>
          </button>
        </div>
      </section>

      <!-- Logo Upload -->
      <section class="config-section">
        <h3 class="section-title">Logo</h3>
        <div class="logo-upload-area"
             :class="{ 'drag-over': dragging }"
             @dragover.prevent="dragging = true"
             @dragleave="dragging = false"
             @drop.prevent="onLogoDrop">
          <img v-if="form.logo_url" :src="form.logo_url" alt="Site logo" class="logo-preview" />
          <div v-else class="logo-placeholder">
            <span class="logo-placeholder-icon">🖼</span>
            <span>Drop PNG/JPG/SVG here, or click to browse</span>
          </div>
          <input
            ref="logoInput"
            type="file"
            accept=".png,.jpg,.jpeg,.svg"
            class="file-input-hidden"
            @change="onLogoFileChange"
          />
          <button class="btn btn-ghost btn-sm logo-browse-btn" type="button" @click="logoInput?.click()">
            Browse…
          </button>
          <p v-if="logoError" class="field-error">{{ logoError }}</p>
        </div>
      </section>

      <!-- Colors -->
      <section class="config-section">
        <h3 class="section-title">Colors</h3>
        <div class="color-grid">
          <div class="color-field">
            <label class="field-label">Primary Accent</label>
            <div class="color-input-wrap">
              <input type="color" v-model="form.primary_color" class="color-swatch" />
              <input type="text" v-model="form.primary_color" class="color-hex" maxlength="7" placeholder="#00e701" />
            </div>
            <p class="field-hint">Main action color (buttons, highlights)</p>
          </div>

          <div class="color-field">
            <label class="field-label">Secondary Accent</label>
            <div class="color-input-wrap">
              <input type="color" v-model="form.secondary_color" class="color-swatch" />
              <input type="text" v-model="form.secondary_color" class="color-hex" maxlength="7" placeholder="#1475e1" />
            </div>
            <p class="field-hint">Links, secondary buttons</p>
          </div>

          <div class="color-field">
            <label class="field-label">Gold / VIP Accent</label>
            <div class="color-input-wrap">
              <input type="color" v-model="form.accent_color" class="color-swatch" />
              <input type="text" v-model="form.accent_color" class="color-hex" maxlength="7" placeholder="#ffd700" />
            </div>
            <p class="field-hint">VIP badges, jackpot highlights</p>
          </div>
        </div>
      </section>

      <!-- Background -->
      <section class="config-section">
        <h3 class="section-title">Background</h3>
        <div class="bg-style-tabs">
          <button
            v-for="style in bgStyles"
            :key="style.value"
            class="bg-style-btn"
            :class="{ active: form.background_style === style.value }"
            type="button"
            @click="form.background_style = style.value"
          >
            {{ style.label }}
          </button>
        </div>

        <!-- Solid color -->
        <div v-if="form.background_style === 'color'" class="bg-option">
          <div class="color-field">
            <label class="field-label">Background Color</label>
            <div class="color-input-wrap">
              <input type="color" v-model="form.background_color" class="color-swatch" />
              <input type="text" v-model="form.background_color" class="color-hex" maxlength="7" />
            </div>
          </div>
        </div>

        <!-- Gradient -->
        <div v-else-if="form.background_style === 'gradient'" class="bg-option">
          <div class="color-grid">
            <div class="color-field">
              <label class="field-label">Gradient Start</label>
              <div class="color-input-wrap">
                <input type="color" v-model="form.background_gradient_start" class="color-swatch" />
                <input type="text" v-model="form.background_gradient_start" class="color-hex" maxlength="7" />
              </div>
            </div>
            <div class="color-field">
              <label class="field-label">Gradient End</label>
              <div class="color-input-wrap">
                <input type="color" v-model="form.background_gradient_end" class="color-swatch" />
                <input type="text" v-model="form.background_gradient_end" class="color-hex" maxlength="7" />
              </div>
            </div>
          </div>
          <div class="gradient-preview"
               :style="{ background: `linear-gradient(135deg, ${form.background_gradient_start}, ${form.background_gradient_end})` }">
            Preview
          </div>
        </div>

        <!-- Image -->
        <div v-else-if="form.background_style === 'image'" class="bg-option">
          <div v-if="form.background_image_url" class="bg-image-preview-wrap">
            <img :src="form.background_image_url" alt="Background" class="bg-image-preview" />
          </div>
          <div class="bg-image-upload">
            <input
              ref="bgImageInput"
              type="file"
              accept=".png,.jpg,.jpeg,.webp"
              class="file-input-hidden"
              @change="onBgImageFileChange"
            />
            <button class="btn btn-ghost btn-sm" type="button" @click="bgImageInput?.click()">
              {{ form.background_image_url ? 'Replace Image…' : 'Upload Background Image…' }}
            </button>
            <p class="field-hint">PNG/JPG/WebP, max 5 MB</p>
            <p v-if="bgImageError" class="field-error">{{ bgImageError }}</p>
          </div>
        </div>
      </section>

      <!-- Typography -->
      <section class="config-section">
        <h3 class="section-title">Typography</h3>
        <div class="font-grid">
          <div class="form-field">
            <label class="field-label">Body Font</label>
            <select v-model="form.font_family" class="select-field">
              <option v-for="font in fontOptions" :key="font.value" :value="font.value">
                {{ font.label }}
              </option>
            </select>
            <p class="font-preview" :style="{ fontFamily: form.font_family }">
              The quick brown fox jumps over the lazy dog
            </p>
          </div>

          <div class="form-field">
            <label class="field-label">Display / Heading Font</label>
            <select v-model="form.font_family_display" class="select-field">
              <option v-for="font in displayFontOptions" :key="font.value" :value="font.value">
                {{ font.label }}
              </option>
            </select>
            <p class="font-preview font-preview--display" :style="{ fontFamily: form.font_family_display }">
              BIG WIN $10,000
            </p>
          </div>
        </div>
      </section>

      <!-- Icon Style -->
      <section class="config-section">
        <h3 class="section-title">Icon Style</h3>
        <p class="section-desc">Choose between native emoji icons or clean SVG icons across the entire site.</p>
        <div class="icon-set-tabs">
          <button
            class="icon-set-btn"
            :class="{ active: form.icon_set === 'emoji' }"
            type="button"
            @click="form.icon_set = 'emoji'"
          >
            <span class="icon-set-preview">&#x1F3B0; &#x1F4B0; &#x1F3C6;</span>
            <span class="icon-set-label">Emoji</span>
            <span class="icon-set-desc">Native emoji characters</span>
          </button>
          <button
            class="icon-set-btn"
            :class="{ active: form.icon_set === 'lucide' }"
            type="button"
            @click="form.icon_set = 'lucide'"
          >
            <span class="icon-set-preview icon-set-preview--svg">
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M6 3h12l4 6-10 13L2 9Z"/><path d="M11 3 8 9l4 13 4-13-3-6"/><path d="M2 9h20"/></svg>
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="12" x2="12" y1="2" y2="22"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6"/><path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18"/><path d="M4 22h16"/><path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22"/><path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22"/><path d="M18 2H6v7a6 6 0 0 0 12 0V2Z"/></svg>
            </span>
            <span class="icon-set-label">Lucide SVG</span>
            <span class="icon-set-desc">Clean vector icons</span>
          </button>
        </div>
      </section>

      <!-- Actions -->
      <div class="config-actions">
        <button
          class="btn btn-primary"
          :disabled="saving"
          type="button"
          @click="saveTheme"
        >
          {{ saving ? 'Saving…' : 'Save Theme' }}
        </button>
        <button class="btn btn-ghost" type="button" :disabled="saving" @click="resetToDefaults">
          Reset to Defaults
        </button>
      </div>

      <div v-if="successMessage" class="success-banner">{{ successMessage }}</div>
      <div v-if="saveError" class="error-banner">{{ saveError }}</div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import apiClient from '@/api/client'

const { t } = useI18n()

interface ThemeConfig {
  logo_url: string | null
  primary_color: string
  secondary_color: string
  accent_color: string
  background_style: 'color' | 'gradient' | 'image'
  background_color: string
  background_gradient_start: string
  background_gradient_end: string
  background_image_url: string | null
  font_family: string
  font_family_display: string
  icon_set: 'emoji' | 'lucide'
}

interface ThemePreset {
  id: string
  name: string
  description: string
  thumbnail_colors: [string, string, string]
  config: Omit<ThemeConfig, 'logo_url' | 'background_image_url'>
}

const DEFAULTS: ThemeConfig = {
  logo_url: null,
  primary_color: '#00e701',
  secondary_color: '#1475e1',
  accent_color: '#ffd700',
  background_style: 'color',
  background_color: '#0f1923',
  background_gradient_start: '#0f1923',
  background_gradient_end: '#1a2c38',
  background_image_url: null,
  font_family: 'Inter',
  font_family_display: 'Russo One',
  icon_set: 'emoji',
}

const form = ref<ThemeConfig>({ ...DEFAULTS })

const loading = ref(true)
const error = ref('')
const saving = ref(false)
const saveError = ref('')
const successMessage = ref('')
const logoError = ref('')
const bgImageError = ref('')
const dragging = ref(false)
const presets = ref<ThemePreset[]>([])
const presetsLoading = ref(true)
const activePresetId = ref<string | null>(null)

const logoInput = ref<HTMLInputElement | null>(null)
const bgImageInput = ref<HTMLInputElement | null>(null)

const bgStyles = [
  { value: 'color', label: 'Solid Color' },
  { value: 'gradient', label: 'Gradient' },
  { value: 'image', label: 'Image' },
] as const

const fontOptions = [
  { value: 'Inter', label: 'Inter (Modern Sans-serif)' },
  { value: 'Poppins', label: 'Poppins (Geometric Sans-serif)' },
  { value: 'Roboto', label: 'Roboto (Clean Sans-serif)' },
  { value: 'Montserrat', label: 'Montserrat (Bold Sans-serif)' },
  { value: 'Oswald', label: 'Oswald (Condensed Sans-serif)' },
  { value: 'Lato', label: 'Lato (Friendly Sans-serif)' },
  { value: '"Playfair Display"', label: 'Playfair Display (Elegant Serif)' },
]

const displayFontOptions = [
  { value: '"Russo One"', label: 'Russo One (Impactful Display)' },
  { value: 'Rajdhani', label: 'Rajdhani (Game Style)' },
  { value: 'Oswald', label: 'Oswald (Strong Condensed)' },
  { value: 'Montserrat', label: 'Montserrat (Bold)' },
  { value: '"Bebas Neue"', label: 'Bebas Neue (Cinematic)' },
  { value: 'Anton', label: 'Anton (Heavy Display)' },
  { value: '"Playfair Display"', label: 'Playfair Display (Luxury Serif)' },
]

async function loadTheme() {
  try {
    const { data } = await apiClient.get<ThemeConfig>('/admin/api/theme')
    form.value = { ...DEFAULTS, ...data }
  } catch {
    error.value = t('admin.settings.theme_failed_load')
  } finally {
    loading.value = false
  }
}

async function loadPresets() {
  try {
    const { data } = await apiClient.get<ThemePreset[]>('/admin/api/theme/presets')
    presets.value = data
  } catch {
    // Non-critical — presets just won't show
  } finally {
    presetsLoading.value = false
  }
}

function applyPreset(preset: ThemePreset) {
  form.value = {
    ...form.value,
    ...preset.config,
  }
  activePresetId.value = preset.id
}

async function saveTheme() {
  saving.value = true
  saveError.value = ''
  successMessage.value = ''

  try {
    const payload = {
      primary_color: form.value.primary_color,
      secondary_color: form.value.secondary_color,
      accent_color: form.value.accent_color,
      background_style: form.value.background_style,
      background_color: form.value.background_color,
      background_gradient_start: form.value.background_gradient_start,
      background_gradient_end: form.value.background_gradient_end,
      font_family: form.value.font_family,
      font_family_display: form.value.font_family_display,
      icon_set: form.value.icon_set,
    }
    await apiClient.post('/admin/api/theme', payload)
    successMessage.value = t('admin.settings.theme_saved')
  } catch {
    saveError.value = t('admin.settings.theme_failed_save')
  } finally {
    saving.value = false
  }
}

function resetToDefaults() {
  form.value = { ...DEFAULTS }
}

function onLogoDrop(event: DragEvent) {
  dragging.value = false
  const file = event.dataTransfer?.files[0]
  if (file) uploadLogo(file)
}

function onLogoFileChange(event: Event) {
  const input = event.target as HTMLInputElement
  const file = input.files?.[0]
  if (file) uploadLogo(file)
}

async function uploadLogo(file: File) {
  logoError.value = ''
  const allowed = ['image/png', 'image/jpeg', 'image/svg+xml']
  if (!allowed.includes(file.type)) {
    logoError.value = t('admin.settings.theme_logo_type_error')
    return
  }
  if (file.size > 2 * 1024 * 1024) {
    logoError.value = t('admin.settings.theme_logo_size_error')
    return
  }

  const fd = new FormData()
  fd.append('logo', file)

  try {
    const { data } = await apiClient.post<{ logo_url: string }>('/admin/api/theme/logo', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    form.value.logo_url = data.logo_url
  } catch {
    logoError.value = t('admin.settings.theme_logo_upload_error')
  }
}

function onBgImageFileChange(event: Event) {
  const input = event.target as HTMLInputElement
  const file = input.files?.[0]
  if (file) uploadBackgroundImage(file)
}

async function uploadBackgroundImage(file: File) {
  bgImageError.value = ''
  const allowed = ['image/png', 'image/jpeg', 'image/webp']
  if (!allowed.includes(file.type)) {
    bgImageError.value = t('admin.settings.theme_bg_type_error')
    return
  }
  if (file.size > 5 * 1024 * 1024) {
    bgImageError.value = t('admin.settings.theme_bg_size_error')
    return
  }

  const fd = new FormData()
  fd.append('background_image', file)

  try {
    const { data } = await apiClient.post<{ background_image_url: string }>(
      '/admin/api/theme/background-image',
      fd,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    )
    form.value.background_image_url = data.background_image_url
  } catch {
    bgImageError.value = t('admin.settings.theme_bg_upload_error')
  }
}

onMounted(() => {
  loadTheme()
  loadPresets()
})
</script>

<style scoped>
.theme-config {
  padding: var(--space-6);
}

.config-header {
  margin-bottom: var(--space-6);
}

.config-title {
  font-size: var(--font-size-h1);
  font-weight: var(--font-weight-bold);
  color: var(--text-primary);
  margin: 0 0 var(--space-1);
}

.config-subtitle {
  font-size: var(--font-size-body);
  color: var(--text-secondary);
  margin: 0;
}

.config-section {
  background: var(--bg-secondary);
  border-radius: var(--radius-lg);
  padding: var(--space-6);
  margin-bottom: var(--space-6);
}

.section-title {
  font-size: var(--font-size-h2);
  font-weight: var(--font-weight-semibold);
  color: var(--text-primary);
  margin: 0 0 var(--space-4);
}

/* Logo */
.logo-upload-area {
  border: 2px dashed var(--bg-hover);
  border-radius: var(--radius-md);
  padding: var(--space-6);
  text-align: center;
  cursor: pointer;
  transition: border-color var(--transition-fast);
  position: relative;
}

.logo-upload-area.drag-over {
  border-color: var(--accent-primary);
  background: var(--color-success-bg);
}

.logo-preview {
  max-height: 80px;
  max-width: 240px;
  object-fit: contain;
  margin-bottom: var(--space-3);
}

.logo-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-2);
  color: var(--text-secondary);
  font-size: var(--font-size-body);
  margin-bottom: var(--space-3);
}

.logo-placeholder-icon {
  font-size: 2rem;
}

.file-input-hidden {
  display: none;
}

.logo-browse-btn {
  margin-top: var(--space-2);
}

/* Colors */
.color-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: var(--space-6);
}

.color-field {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.color-input-wrap {
  display: flex;
  align-items: center;
  gap: var(--space-3);
}

.color-swatch {
  width: 44px;
  height: 44px;
  border: none;
  border-radius: var(--radius-sm);
  cursor: pointer;
  padding: 2px;
  background: var(--bg-tertiary);
  flex-shrink: 0;
}

.color-hex {
  flex: 1;
  background: var(--bg-input);
  border: 1px solid var(--bg-hover);
  border-radius: var(--radius-sm);
  color: var(--text-primary);
  font-size: var(--font-size-body);
  font-family: var(--font-family-mono);
  padding: var(--space-2) var(--space-3);
  height: 44px;
}

.color-hex:focus {
  outline: none;
  border-color: var(--accent-primary);
}

/* Background */
.bg-style-tabs {
  display: flex;
  gap: var(--space-2);
  margin-bottom: var(--space-4);
}

.bg-style-btn {
  padding: var(--space-2) var(--space-4);
  border: 1px solid var(--bg-hover);
  border-radius: var(--radius-sm);
  background: var(--bg-tertiary);
  color: var(--text-secondary);
  font-size: var(--font-size-body);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.bg-style-btn.active {
  border-color: var(--accent-primary);
  background: var(--color-success-bg);
  color: var(--accent-primary);
}

.bg-option {
  padding-top: var(--space-4);
}

.gradient-preview {
  height: 80px;
  border-radius: var(--radius-md);
  margin-top: var(--space-4);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-primary);
  font-size: var(--font-size-sm);
  opacity: 0.8;
}

.bg-image-preview-wrap {
  margin-bottom: var(--space-4);
}

.bg-image-preview {
  max-height: 120px;
  max-width: 100%;
  border-radius: var(--radius-md);
  object-fit: cover;
}

.bg-image-upload {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

/* Typography */
.font-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--space-6);
}

.form-field {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.select-field {
  background: var(--bg-input);
  border: 1px solid var(--bg-hover);
  border-radius: var(--radius-sm);
  color: var(--text-primary);
  font-size: var(--font-size-body);
  padding: var(--space-2) var(--space-3);
  height: 44px;
  cursor: pointer;
}

.select-field:focus {
  outline: none;
  border-color: var(--accent-primary);
}

.font-preview {
  font-size: var(--font-size-body);
  color: var(--text-secondary);
  margin: 0;
  padding: var(--space-3);
  background: var(--bg-tertiary);
  border-radius: var(--radius-sm);
}

.font-preview--display {
  font-size: var(--font-size-h2);
  font-weight: var(--font-weight-bold);
  color: var(--accent-gold);
}

/* Actions */
.config-actions {
  display: flex;
  gap: var(--space-3);
  margin-bottom: var(--space-4);
}

/* Shared */
.field-label {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.field-hint {
  font-size: var(--font-size-sm);
  color: var(--text-tertiary);
  margin: 0;
}

.field-error {
  font-size: var(--font-size-sm);
  color: var(--color-danger);
  margin: 0;
}

.loading-state {
  padding: var(--space-8);
  text-align: center;
  color: var(--text-secondary);
}

.error-banner {
  background: var(--color-danger-bg);
  border: 1px solid var(--color-danger-border);
  color: var(--color-danger);
  border-radius: var(--radius-md);
  padding: var(--space-3) var(--space-4);
  margin-bottom: var(--space-4);
}

.success-banner {
  background: var(--color-success-bg);
  border: 1px solid var(--color-success-border);
  color: var(--color-success);
  border-radius: var(--radius-md);
  padding: var(--space-3) var(--space-4);
  margin-bottom: var(--space-4);
}

.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: var(--space-2) var(--space-5);
  border-radius: var(--radius-md);
  font-size: var(--font-size-body);
  font-weight: var(--font-weight-medium);
  cursor: pointer;
  border: none;
  transition: all var(--transition-fast);
  min-height: 44px;
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-primary {
  background: var(--accent-primary);
  color: var(--color-on-accent);
}

.btn-primary:hover:not(:disabled) {
  background: var(--accent-primary-hover);
}

.btn-ghost {
  background: var(--bg-tertiary);
  color: var(--text-primary);
  border: 1px solid var(--bg-hover);
}

.btn-ghost:hover:not(:disabled) {
  background: var(--bg-hover);
}

.btn-sm {
  padding: var(--space-1) var(--space-3);
  min-height: 36px;
  font-size: var(--font-size-sm);
}

/* Presets */
.section-desc {
  font-size: var(--font-size-sm);
  color: var(--text-secondary);
  margin: 0 0 var(--space-4);
}

.presets-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: var(--space-3);
}

.preset-card {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  padding: var(--space-3);
  background: var(--bg-tertiary);
  border: 2px solid transparent;
  border-radius: var(--radius-md);
  cursor: pointer;
  text-align: left;
  transition: border-color var(--transition-fast), background var(--transition-fast);
}

.preset-card:hover {
  border-color: var(--bg-hover);
  background: var(--bg-hover);
}

.preset-card--active {
  border-color: var(--accent-primary);
  background: var(--color-success-bg);
}

.preset-swatches {
  display: flex;
  gap: var(--space-1);
  margin-bottom: var(--space-1);
}

.preset-swatch {
  width: 24px;
  height: 24px;
  border-radius: var(--radius-sm);
  flex-shrink: 0;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.preset-name {
  font-size: var(--font-size-body);
  font-weight: var(--font-weight-semibold);
  color: var(--text-primary);
}

.preset-desc {
  font-size: var(--font-size-sm);
  color: var(--text-tertiary);
  line-height: var(--line-height-body);
}

.loading-state--sm {
  padding: var(--space-4);
  font-size: var(--font-size-sm);
}

/* Icon Set */
.icon-set-tabs {
  display: flex;
  gap: var(--space-3);
}

.icon-set-btn {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-4);
  background: var(--bg-tertiary);
  border: 2px solid transparent;
  border-radius: var(--radius-md);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.icon-set-btn:hover {
  border-color: var(--bg-hover);
  background: var(--bg-hover);
}

.icon-set-btn.active {
  border-color: var(--accent-primary);
  background: var(--color-success-bg);
}

.icon-set-preview {
  font-size: 1.25rem;
  display: flex;
  gap: var(--space-2);
  min-height: 28px;
  align-items: center;
}

.icon-set-preview--svg {
  color: var(--text-primary);
}

.icon-set-label {
  font-size: var(--font-size-body);
  font-weight: var(--font-weight-semibold);
  color: var(--text-primary);
}

.icon-set-desc {
  font-size: var(--font-size-sm);
  color: var(--text-tertiary);
}

@media (max-width: 640px) {
  .color-grid {
    grid-template-columns: 1fr;
  }

  .font-grid {
    grid-template-columns: 1fr;
  }

  .presets-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}
</style>
